import React, { useState, useEffect } from "react";
import { X, Minus, Plus, ShoppingBag, Trash2, CheckCircle2, FileText, Download, Building, User, CreditCard, ArrowLeft, Calendar, Clock, Sparkles, Moon, Sun, MapPin, Truck, Utensils, Hourglass, ClipboardList, MessageSquare, Store, CalendarDays } from "lucide-react";
import DinnerMoonIcon from "./DinnerMoonIcon";
import LunchSunIcon from "./LunchSunIcon";
import PickupStoreIcon from "./PickupStoreIcon";

// ==========================================
// 1. ADD ORDER MODAL (WITH CONFIRMATION SUB-STEP)
// ==========================================
export function AddOrderModal({
  isOpen,
  onClose,
  menuItems,
  onAddOrderSubmit,
  preselectedMeal = null,
  currencySymbol = ""
}) {
  if (!isOpen) return null;

  const [selectedMeal, setSelectedMeal] = useState(preselectedMeal || menuItems[0]);
  const [quantity, setQuantity] = useState(1);
  const [step, setStep] = useState(1); // 1: Select & Qty, 2: Confirmation
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync state if preselectedMeal changes
  useEffect(() => {
    if (preselectedMeal) {
      setSelectedMeal(preselectedMeal);
      setQuantity(1);
      setStep(1);
    }
  }, [preselectedMeal]);

  const handleQuantityChange = (type) => {
    if (type === "dec" && quantity > 1) {
      setQuantity(prev => prev - 1);
    } else if (type === "inc" && quantity < 10) {
      setQuantity(prev => prev + 1);
    }
  };

  const handleMealChange = (e) => {
    const mealId = e.target.value;
    const meal = menuItems.find(item => item.id === mealId);
    if (meal) setSelectedMeal(meal);
  };

  const totalAmount = selectedMeal ? selectedMeal.price * quantity : 0;

  const handlePlaceOrder = () => {
    setStep(2); // Go to confirmation step
  };

  const handleConfirmOrder = () => {
    setIsSubmitting(true);
    // Simulate API submission
    setTimeout(() => {
      onAddOrderSubmit({
        mealId: selectedMeal.id,
        name: selectedMeal.name,
        category: selectedMeal.category,
        price: selectedMeal.price,
        quantity: quantity,
        total: totalAmount,
        image: selectedMeal.image,
        description: selectedMeal.description
      });
      setIsSubmitting(false);
      setStep(1);
      setQuantity(1);
      onClose();
    }, 1500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-white rounded-xl border border-border-primary shadow-2xl max-w-lg w-full overflow-hidden transition-all duration-300 transform scale-100 flex flex-col max-h-[90vh]">

        {/* Modal Header */}
        <div className="flex items-center justify-between p-5 border-b border-border-primary bg-bg-secondary">
          <div className="flex items-center space-x-2">
            <ShoppingBag className="w-5 h-5 text-brand-red" />
            <h3 className="text-base font-bold text-text-primary font-display">
              {step === 1 ? "Add New Order" : "Confirm Your Order"}
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-text-muted hover:text-text-primary rounded-full p-1 hover:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-5">

          {/* STEP 1: CONFIGURE MEAL & QUANTITY */}
          {step === 1 && (
            <>
              {/* Select Meal dropdown if not preselected */}
              {!preselectedMeal && (
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-text-secondary">Choose a Meal</label>
                  <select
                    value={selectedMeal.id}
                    onChange={handleMealChange}
                    className="w-full p-2.5 bg-bg-secondary border border-border-primary rounded-lg text-sm text-text-primary focus:outline-none focus:ring-0.5 focus:ring-brand-red focus:border-brand-red cursor-pointer"
                  >
                    {menuItems.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.name} ({item.currency_symbol || currencySymbol}{item.price.toFixed(2)})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Meal Highlight Card */}
              {selectedMeal && (
                <div className="p-4 rounded-lg bg-bg-secondary/40 border border-border-primary flex flex-col text-left">
                  <div className="space-y-1">
                    <div>
                      <span className="text-[9px] font-bold uppercase tracking-wider bg-brand-red-light text-brand-red px-2 py-1 rounded border border-brand-red/10 w-fit">
                        {selectedMeal.category}
                      </span>
                    </div>
                    <h4 className="text-sm font-bold text-text-primary mt-2 leading-tight">{selectedMeal.name}</h4>
                    <p className="text-xs text-text-secondary leading-snug">{selectedMeal.description}</p>
                    <span className="text-sm font-extrabold text-brand-red font-mono block pt-1">
                      {selectedMeal.currency_symbol || currencySymbol}{selectedMeal.price.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              {/* Quantity Selector */}
              <div className="flex items-center justify-between p-4 bg-bg-secondary/50 rounded-lg border border-border-primary">
                <div>
                  <span className="text-xs font-semibold text-text-secondary">Quantity</span>
                  <p className="text-[10px] text-text-muted mt-0.5">Maximum 10 meals per order</p>
                </div>
                <div className="flex items-center space-x-3 bg-white border border-border-primary rounded-lg p-1.5">
                  <button
                    onClick={() => handleQuantityChange("dec")}
                    className="w-8 h-8 rounded-md hover:bg-bg-secondary flex items-center justify-center text-text-secondary active:scale-90 transition-all font-semibold"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-sm font-bold text-text-primary font-mono w-6 text-center select-none">
                    {quantity}
                  </span>
                  <button
                    onClick={() => handleQuantityChange("inc")}
                    className="w-8 h-8 rounded-md hover:bg-bg-secondary flex items-center justify-center text-text-secondary active:scale-90 transition-all font-semibold"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Total Calculation */}
              <div className="flex items-center justify-between pt-4 border-t border-border-primary">
                <span className="text-sm font-bold text-text-secondary">Total Amount</span>
                <span className="text-xl font-extrabold text-brand-red font-mono">
                  {selectedMeal?.currency_symbol || currencySymbol}{totalAmount.toFixed(2)}
                </span>
              </div>
            </>
          )}

          {/* STEP 2: ORDER CONFIRMATION */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center py-2">
                <div className="w-12 h-12 bg-brand-red-light text-brand-red rounded-full flex items-center justify-center mx-auto mb-3">
                  <ShoppingBag className="w-6 h-6" />
                </div>
                <h4 className="text-base font-bold text-text-primary">Confirm your order?</h4>
                <p className="text-xs text-text-secondary mt-1">
                  You are about to place an order for the following item:
                </p>
              </div>

              <div className="bg-bg-secondary rounded-lg border border-border-primary p-4 space-y-3.5">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-[10px] text-text-muted uppercase font-bold">Meal Item</span>
                    <p className="text-sm font-bold text-text-primary leading-tight mt-0.5">{selectedMeal.name}</p>
                    <span className="text-xs text-text-secondary font-mono">{selectedMeal.category}</span>
                  </div>
                  <span className="text-xs font-semibold bg-white border border-border-primary px-2.5 py-1 rounded-md">
                    Qty: {quantity}
                  </span>
                </div>

                <div className="border-t border-border-primary pt-3.5 flex justify-between items-center text-sm">
                  <span className="font-semibold text-text-secondary">Unit Price</span>
                  <span className="font-bold text-text-primary font-mono">{selectedMeal?.currency_symbol || currencySymbol}{selectedMeal.price.toFixed(2)}</span>
                </div>

                <div className="border-t border-dashed border-border-primary pt-3.5 flex justify-between items-center">
                  <span className="text-sm font-bold text-text-primary">Total Bill</span>
                  <span className="text-lg font-extrabold text-brand-red font-mono">{selectedMeal?.currency_symbol || currencySymbol}{totalAmount.toFixed(2)}</span>
                </div>
              </div>

              <p className="text-[10px] text-text-muted leading-relaxed text-center">
                By confirming, this meal will be added to today's order scheduling. You can cancel it anytime before the 12:00 PM cutoff time.
              </p>
            </div>
          )}

        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-border-primary bg-bg-secondary flex gap-3">
          {step === 1 ? (
            <>
              <button
                onClick={onClose}
                className="w-1/2 py-2.5 px-4 bg-white border border-border-primary text-text-secondary text-sm font-bold rounded-lg hover:bg-gray-100 transition-colors active:scale-98"
              >
                Cancel
              </button>
              <button
                onClick={handlePlaceOrder}
                className="w-1/2 py-2.5 px-4 bg-brand-red text-white text-sm font-bold rounded-lg hover:bg-brand-red-hover shadow-sm transition-colors active:scale-98"
              >
                Place Order
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep(1)}
                disabled={isSubmitting}
                className="w-1/2 py-2.5 px-4 bg-white border border-border-primary text-text-secondary text-sm font-bold rounded-lg hover:bg-gray-100 transition-colors active:scale-98 disabled:opacity-50"
              >
                Go Back
              </button>
              <button
                onClick={handleConfirmOrder}
                disabled={isSubmitting}
                className="w-1/2 py-2.5 px-4 bg-brand-red text-white text-sm font-bold rounded-lg hover:bg-brand-red-hover shadow-sm transition-colors active:scale-98 disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    <span>Placing Order...</span>
                  </>
                ) : (
                  <span>Confirm Order</span>
                )}
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
}


// ==========================================
// 2. CANCEL ORDER CONFIRMATION MODAL
// ==========================================
export function CancelOrderModal({ isOpen, onClose, order, onCancelSubmit, currencySymbol = "" }) {
  if (!isOpen || !order) return null;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("Changed my mind");

  const handleConfirmCancel = () => {
    setIsSubmitting(true);
    onCancelSubmit(order, cancellationReason);
    setIsSubmitting(false);
  };

  const totalVal = order.total !== undefined ? order.total : (order.total_amount || 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-white rounded-xl border border-border-primary shadow-2xl max-w-md w-full overflow-hidden animate-modal-scale">

        {/* Header */}
        <div className="p-5 border-b border-border-primary bg-bg-secondary flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Trash2 className="w-5 h-5 text-brand-red" />
            <h3 className="text-base font-bold text-text-primary font-display">Cancel Order</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-text-muted hover:text-text-primary rounded-full p-1 hover:bg-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="text-center pb-1">
            <h4 className="text-base font-bold text-text-primary">Cancel this order?</h4>
            <p className="text-xs text-text-secondary mt-1">
              You are about to cancel your scheduled meal slot.
            </p>
          </div>

          <div className="bg-brand-red-light/20 rounded-lg border border-brand-red/10 p-4 space-y-2">
            <div className="flex justify-between items-center text-xs">
              <span className="font-semibold text-text-secondary">Meal Name</span>
              <span className="font-bold text-text-primary">{order.name || order.meal_name}</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="font-semibold text-text-secondary">Quantity</span>
              <span className="font-bold text-text-primary font-mono">{order.quantity}x</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="font-semibold text-text-secondary">Total Value</span>
              <span className="font-extrabold text-brand-red font-mono">{order.currency_symbol || currencySymbol}{totalVal.toFixed(2)}</span>
            </div>
          </div>

          {/* Cancellation Reason Dropdown */}
          <div className="space-y-1.5 text-left">
            <label className="text-xs font-bold text-text-primary block">
              Reason for Cancellation
            </label>
            <select
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              disabled={isSubmitting}
              className="w-full text-xs font-medium text-text-primary bg-bg-secondary border border-border-primary rounded-lg p-2.5 outline-none focus:border-brand-red transition-colors"
            >
              <option value="Changed my mind">Changed my mind</option>
              <option value="Plans changed">Plans changed</option>
              <option value="Ordered by mistake">Ordered by mistake</option>
              <option value="Delivery time not suitable">Delivery time not suitable</option>
              <option value="Other">Other reason</option>
            </select>
          </div>

          <div className="flex items-start space-x-2 p-3 bg-warning-bg/30 border border-warning/10 rounded-lg text-xs text-text-secondary">
            <span className="font-bold text-warning uppercase shrink-0 mt-0.5 mr-1 text-[10px] bg-white px-1.5 py-1 rounded border border-warning/20">Important</span>
            <p className="leading-snug">This action cannot be undone. Once cancelled, your scheduled meal slot will be released.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border-primary bg-bg-secondary flex gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="w-1/2 py-2.5 px-4 bg-white border border-border-primary text-text-secondary text-sm font-bold rounded-lg hover:bg-gray-100 transition-colors active:scale-98"
          >
            Keep Order
          </button>
          <button
            onClick={handleConfirmCancel}
            disabled={isSubmitting}
            className="w-1/2 py-2.5 px-4 bg-brand-red text-white text-sm font-bold rounded-lg hover:bg-brand-red-hover shadow-sm transition-colors active:scale-98 flex items-center justify-center space-x-2"
          >
            {isSubmitting ? (
              <>
                <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                <span>Cancelling...</span>
              </>
            ) : (
              <span>Cancel Order</span>
            )}
          </button>
        </div>

      </div>
    </div>
  );
}


// ==========================================
// 3. ORDER DETAILS DRAWER / MODAL
// ==========================================
export function OrderDetailsDrawer({ isOpen, onClose, order, isLoading, onReorderClick, isCustomerVerified, isOrderingClosed, currencySymbol = "" }) {
  if (!isOpen || (!order && !isLoading)) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/80">
      {/* Sidebar-style Drawer content */}
      <div className="bg-white w-full max-w-md h-full shadow-2xl flex flex-col justify-between border-l border-border-primary animate-drawer-slide">

        {/* Drawer Header */}
        <div className="p-5 border-b border-border-primary bg-bg-secondary flex justify-between items-center">
          <div className="space-y-0.5">
            <h3 className="text-base font-bold text-text-primary font-display">Order Details</h3>
            {!isLoading && <p className="text-[10px] text-text-muted font-mono">#{order.order_unique_id || order.id}</p>}
          </div>
          <button
            onClick={onClose}
            className="text-text-muted hover:text-text-primary rounded-full p-1 hover:bg-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Drawer Body */}
        <div className="p-6 flex-1 overflow-y-auto space-y-6 text-sm">
          {isLoading ? (
            /* Premium Shimmer Loading Skeleton */
            <div className="space-y-6">
              <div className="shimmer h-16 border border-border-primary rounded-lg"></div>
              <div className="space-y-3">
                <div className="shimmer h-4 w-1/3 rounded"></div>
                <div className="shimmer h-24 border border-border-primary rounded-lg"></div>
              </div>
              <div className="space-y-3">
                <div className="shimmer h-4 w-1/3 rounded"></div>
                <div className="shimmer h-40 border border-border-primary rounded-lg"></div>
              </div>
            </div>
          ) : (
            <>
              {/* Status highlight */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-bg-secondary/50 border border-border-primary">
                <div>
                  <span className="text-[10px] text-text-muted uppercase font-semibold">Status</span>
                  <p className="text-sm font-bold text-text-primary mt-0.5">{order.status}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold border ${order.status === "Completed" || order.status === "Delivered"
                  ? "bg-success-bg text-success border-success/20"
                  : order.status === "Cancelled" || order.status === "Canceled"
                    ? "bg-brand-red-light text-brand-red border-brand-red/10"
                    : order.status === "Refunded"
                      ? "bg-blue-50 text-blue-700 border-blue-200"
                      : "bg-warning-bg text-warning border-warning/20"
                  }`}>
                  {order.status}
                </span>
              </div>

              {/* Meal summary */}
              <div className="space-y-3">
                <span className="text-xs uppercase tracking-wider font-bold text-text-secondary block">Meal Information</span>
                <div className="p-4 border border-border-primary rounded-lg bg-bg-secondary/20 flex flex-col text-left space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-bold uppercase tracking-wider bg-bg-secondary text-text-secondary border border-border-primary px-1.5 py-1 rounded flex items-center gap-1">
                      {order.meal_type === "dinner" || order.category?.toLowerCase() === "dinner" ? (
                        <DinnerMoonIcon className="w-2.5 h-2.5 text-text-secondary" />
                      ) : (
                        <LunchSunIcon className="w-2.5 h-2.5 text-text-secondary" />
                      )}
                      {order.category || order.meal_type}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-text-primary">{order.meal_name || order.name}</h4>
                    {(order.meal_description || order.description) && (
                      <p className="text-xs text-text-secondary leading-snug mt-1.5">{order.meal_description || order.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-text-secondary pt-1 border-t border-border-primary/40">
                    <Clock className="w-3.5 h-3.5 text-text-secondary" />
                    <span className="font-medium">{order.time_slot || order.orderTime}</span>
                  </div>
                </div>
              </div>

              {/* Delivery Information */}
              <div className="space-y-3">
                <span className="text-xs uppercase tracking-wider font-bold text-text-secondary block">Delivery Details</span>
                <div className="p-4 border border-border-primary rounded-lg bg-bg-secondary/20 flex flex-col text-left space-y-3">
                  <div className="flex items-center gap-2 text-xs text-text-primary">
                    {order.delivery_preference === "Pickup" ? (
                      <PickupStoreIcon className="w-3.5 h-3.5 text-text-secondary" />
                    ) : (
                      <Truck className="w-3.5 h-3.5 text-text-secondary" />
                    )}
                    <span className="font-bold">Preference: {order.delivery_preference || "Standard Delivery"}</span>
                  </div>
                  {order.delivery_address && (
                    <div className="flex gap-2 text-xs text-text-secondary pt-1.5 border-t border-border-primary/40">
                      <MapPin className="w-3.5 h-3.5 text-text-secondary shrink-0 mt-0.5" />
                      <span>{order.delivery_address.address_1 || `${order.delivery_address.address_2 || ""}, ${order.delivery_address.city || ""}, ${order.delivery_address.province || ""}`}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Order facts */}
              <div className="space-y-3">
                <span className="text-xs uppercase tracking-wider font-bold text-text-secondary block">Billing Facts</span>
                <div className="divide-y divide-border-primary border border-border-primary rounded-lg overflow-hidden bg-white">
                  <div className="p-3 flex justify-between">
                    <span className="text-xs text-text-secondary">Order ID</span>
                    <span className="text-xs font-mono font-semibold text-text-primary">#{order.order_unique_id || order.id}</span>
                  </div>
                  <div className="p-3 flex justify-between items-center">
                    <span className="text-xs text-text-secondary">Order Date</span>
                    <span className="text-xs font-semibold text-text-primary flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5 text-text-secondary" />
                      {order.order_date || order.orderDate || "Today"}
                    </span>
                  </div>
                  <div className="p-3 flex justify-between">
                    <span className="text-xs text-text-secondary">Quantity</span>
                    <span className="text-xs font-mono font-bold text-text-primary">{order.quantity}x</span>
                  </div>
                  <div className="p-3 flex justify-between">
                    <span className="text-xs text-text-secondary">Unit Price</span>
                    <span className="text-xs font-mono font-semibold text-text-primary">
                      {order.currency_symbol || currencySymbol}{(order.total_amount ? (order.total_amount / order.quantity) : (order.price * 30)).toFixed(2)}
                    </span>
                  </div>
                  <div className="p-3 flex justify-between bg-bg-secondary/40 items-center">
                    <span className="text-xs font-bold text-text-primary">Total Amount</span>
                    <span className="text-sm font-extrabold text-brand-red font-mono">
                      {order.currency_symbol || currencySymbol}{(order.total_amount || order.total).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Drawer Footer */}
        <div className="p-4 border-t border-border-primary bg-bg-secondary">
          <button
            onClick={onClose}
            className="w-full py-2.5 px-4 bg-white border border-border-primary text-text-secondary text-sm font-bold rounded-lg hover:bg-gray-100 transition-colors active:scale-98"
          >
            Close Details
          </button>
        </div>

      </div>
    </div>
  );
}


// ==========================================
// 4. INVOICE DETAILS MODAL (REAL PAYMENT DOCUMENT)
// ==========================================
export function InvoiceDetailsModal({ isOpen, onClose, invoice, isLoading = false, provider, customer, onDownload, currencySymbol = "" }) {
  if (!isOpen || (!invoice && !isLoading)) return null;

  const customerName = invoice?.billing_info?.customer?.customer_name || customer.name === "Muskan" ? "Ahim" : customer.name;
  const customerIdVal = invoice?.billing_info?.customer?.service_provider_customer_id || (customer.id === "CST-8892" ? "#1826" : customer.id);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const dateObj = new Date(dateStr);
    return dateObj.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getTransactionId = (inv) => {
    if (inv.bill_unique_id) return inv.bill_unique_id;
    const suffix = inv.id ? inv.id.replace("INV-", "") : "";
    return `TTBLIN2608000${suffix.slice(-2)}`;
  };

  const getMealCategory = (mealName = "") => {
    const name = mealName.toLowerCase();
    if (name.includes("salmon") || name.includes("keto salad") || name.includes("rib") || name.includes("dinner")) return "DINNER";
    return "LUNCH";
  };

  const getTimeSlot = (category) => {
    if (category === "DINNER") return "07:30 PM - 09:00 PM";
    return "02:00 PM - 03:00 PM";
  };

  const isApiInvoice = invoice && (invoice.bill_unique_id !== undefined || invoice.raw_bill_id !== undefined || invoice.id !== undefined);

  const mealCategory = (
    invoice?.billing_info?.meal_timing ||
    invoice?.meal_timing ||
    invoice?.category ||
    invoice?.billing_info?.category ||
    (invoice?.billing_info?.meal_name ? getMealCategory(invoice.billing_info.meal_name) : null) ||
    (invoice?.meal_name ? getMealCategory(invoice.meal_name) : null) ||
    "LUNCH"
  ).toUpperCase();

  const formatMealTitle = (titleStr, category) => {
    const raw = titleStr || (category === "DINNER" ? "Standard Dinner Plan" : "Standard Lunch Plan");
    let cleaned = raw.replace(/_/g, ' ').replace(/\(.*\)/g, '').trim();
    cleaned = cleaned.replace(/\bplan\s+plan\b/gi, 'Plan');
    if (cleaned.toLowerCase() === "meal plan" || cleaned.toLowerCase() === "meal") {
      return "Meal Plan";
    }
    if (cleaned.toLowerCase() === "standard") {
      return "Standard Meal Plan";
    }
    return cleaned
      .split(' ')
      .filter(Boolean)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  };

  const rawMealTitle =
    invoice?.billing_info?.meal_name ||
    invoice?.billing_info?.meal_plan_name ||
    invoice?.billing_info?.title ||
    invoice?.meal_name ||
    invoice?.meal_plan_name ||
    invoice?.title ||
    invoice?.orderDesc ||
    invoice?.bill_type;

  const mealTitle = formatMealTitle(rawMealTitle, mealCategory);

  const timeSlot =
    invoice?.billing_info?.time_slot ||
    invoice?.billing_info?.slot ||
    invoice?.time_slot ||
    invoice?.slot ||
    getTimeSlot(mealCategory);

  const deliveryPreference =
    invoice?.billing_info?.delivery_preference ||
    invoice?.delivery_preference ||
    invoice?.preference ||
    "Pickup";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 overflow-y-auto">
      <div className="bg-white rounded-2xl p-4 border border-border-primary shadow-2xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[92vh] animate-modal-scale">

        {/* Header */}
        <div className="flex items-center justify-between p-2 border-b border-border-primary bg-bg-secondary shrink-0">
          <div className="flex items-center">
            <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded-full transition-colors mr-2 cursor-pointer">
              <ArrowLeft className="w-5 h-5 text-text-primary" />
            </button>
            <h3 className="text-base font-bold text-text-primary font-display">
              {!isLoading && `Invoice #${(invoice?.bill_unique_id || invoice?.id || '').replace("INV-", "").replace("TTBLIN", "")}`}
              {isLoading && "Loading Invoice..."}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-text-muted hover:text-text-primary rounded-full p-1 hover:bg-gray-200 cursor-pointer transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Invoice Body (Document format) */}
        <div className="p-5 overflow-y-auto flex-1 bg-[#FAF9F6]">
          {isLoading ? (
            /* Premium Invoice Shimmer Loader */
            <div className="border border-dashed border-border-primary bg-white rounded-2xl p-5 space-y-4">
              <div className="flex justify-between items-start">
                <div className="space-y-2 w-1/3">
                  <div className="shimmer h-4 rounded"></div>
                  <div className="shimmer h-3 rounded w-2/3"></div>
                </div>
                <div className="shimmer h-6 rounded w-16"></div>
              </div>
              <div className="shimmer h-20 rounded-xl"></div>
              <div className="shimmer h-14 rounded-xl"></div>
              <div className="shimmer h-24 rounded-xl"></div>
            </div>
          ) : (
            /* Dashed outer card */
            <div className="border border-dashed border-brand-red/35 bg-white rounded-2xl p-3 space-y-4 shadow-sm">

              {/* Top row: brand and status */}
              <div className="flex justify-between items-start">
                <div className="text-left">
                  <h4 className="text-base font-black tracking-wider text-brand-red font-display">INVOICE</h4>
                  <span className="text-[10px] font-mono text-text-muted block mt-0.5">
                    {getTransactionId(invoice)}
                  </span>
                </div>
                <span className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${invoice?.status?.toLowerCase() === "paid"
                  ? "bg-[#E8F8F0] text-[#10B981] border-[#10B981]/25"
                  : invoice?.status?.toLowerCase() === "pending"
                    ? "bg-warning-bg text-warning border-warning/20"
                    : "bg-brand-red-light text-brand-red border border-brand-red/10"
                  }`}>
                  {invoice?.status || "Paid"}
                </span>
              </div>

              {/* Dates card */}
              <div className="border border-border-primary/60 rounded-xl p-3 bg-[#FCFCFA] space-y-2 text-xs text-text-secondary text-left">
                <div className="flex justify-between">
                  <span className="text-text-muted">Invoice Date:</span>
                  <span className="font-semibold text-text-primary">
                    {formatDate(invoice?.bill_date || invoice?.date)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-muted">Billing Period:</span>
                  <span className="font-semibold text-text-primary">
                    {formatDate(invoice?.billing_period_start || invoice?.date)} - {formatDate(invoice?.billing_period_end || invoice?.date)}
                  </span>
                </div>
                {invoice?.settled_at && (
                  <div className="flex justify-between">
                    <span className="text-text-muted">Settled At:</span>
                    <span className="font-semibold text-text-primary">
                      {formatDate(invoice.settled_at)}
                    </span>
                  </div>
                )}
              </div>

              {/* Billed To card */}
              <div className="border border-border-primary/60 rounded-xl p-3 bg-[#FCFCFA] text-left">
                <span className="text-[9px] uppercase tracking-wider font-extrabold text-text-muted block mb-1">BILLED TO:</span>
                <p className="text-xs font-bold text-text-primary">{customerName}</p>
                {/* <p className="text-[10px] text-text-secondary mt-0.5">Customer ID: {customerIdVal}</p> */}
              </div>

              {/* Meal Details card(s) */}
              {isApiInvoice ? (
                <div className="border border-border-primary/60 rounded-xl p-3 bg-[#FCFCFA] text-left space-y-2.5">
                  <div className="flex justify-between items-center gap-2 border-b border-border-primary/30 pb-2">
                    <div>
                      <span className="text-[9px] uppercase tracking-wider font-extrabold text-text-muted block">MEAL DETAILS</span>
                      <h5 className="text-xs font-bold text-text-primary leading-tight mt-0.5">
                        {mealTitle}
                      </h5>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider border shrink-0 ${mealCategory === "LUNCH"
                      ? "bg-[#FCE8E6] text-brand-red border-brand-red/10"
                      : "bg-[#EBE8FC] text-[#6366F1] border-[#6366F1]/10"
                      }`}>
                      {mealCategory}
                    </span>
                  </div>
                  <div className="space-y-1.5 pt-0.5 text-xs text-text-secondary">
                    <div className="flex justify-between items-center">
                      <span className="text-text-muted">Time Slot:</span>
                      <span className="font-semibold text-text-primary">{timeSlot}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-text-muted">Preference:</span>
                      <span className="font-semibold text-text-primary capitalize">{deliveryPreference}</span>
                    </div>
                  </div>
                </div>
              ) : invoice?.meals ? (
                invoice.meals.map((meal, idx) => {
                  const category = getMealCategory(meal.name);
                  const displayName = meal.name === "Grilled Chicken Rice Bowl" ? "Kitchenfin special thali" : meal.name;
                  const badgeColor =
                    category === "LUNCH" ? "bg-[#FCE8E6] text-brand-red border-brand-red/10" :
                      "bg-[#EBE8FC] text-[#6366F1] border-[#6366F1]/10";

                  return (
                    <div key={idx} className="border border-border-primary/60 rounded-xl p-3 bg-[#FCFCFA] text-left space-y-2">
                      <span className="text-[9px] uppercase tracking-wider font-extrabold text-text-muted block">MEAL DETAILS</span>
                      <div className="flex justify-between items-start gap-2">
                        <span className="text-xs font-bold text-text-primary leading-tight">{displayName}</span>
                        <span className={`px-1.5 py-1 rounded text-[8px] font-black uppercase tracking-wider border shrink-0 ${badgeColor}`}>
                          {category}
                        </span>
                      </div>
                      <div className="space-y-1.5 pt-1 text-xs text-text-secondary">
                        <div className="flex justify-between">
                          <span className="text-text-muted">Time Slot:</span>
                          <span className="font-semibold text-text-primary">{getTimeSlot(category)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-text-muted">Preference:</span>
                          <span className="font-semibold text-text-primary">Pickup</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="border border-border-primary/60 rounded-xl p-3 bg-[#FCFCFA] text-left space-y-2">
                  <span className="text-[9px] uppercase tracking-wider font-extrabold text-text-muted block font-mono">ORDER REFERENCE</span>
                  <div className="flex justify-between items-start">
                    <span className="text-xs font-bold text-text-primary">Order ID: #{invoice?.raw_order_id || invoice?.id}</span>
                    <span className="text-[10px] text-text-secondary font-mono">{invoice?.bill_type || "Standard"}</span>
                  </div>
                </div>
              )}

              {/* Payment Summary card */}
              <div className="border border-border-primary/60 rounded-xl p-3 bg-[#FCFCFA] text-left space-y-2 text-xs text-text-secondary">
                <span className="text-[9px] uppercase tracking-wider font-extrabold text-text-muted block mb-1">PAYMENT SUMMARY</span>
                <div className="flex justify-between">
                  <span>Meal Cost:</span>
                  <span className="font-mono font-medium text-text-primary">
                    {invoice?.currency_symbol || provider?.currencySymbol || currencySymbol}{(invoice.billing_info?.meal_cost !== undefined ? invoice.billing_info.meal_cost : (isApiInvoice ? invoice.total_amount : (invoice.amount * 30))).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Quantity of Orders:</span>
                  <span className="font-mono font-medium text-text-primary">
                    {invoice.orders_count || (invoice.meals ? invoice.meals.reduce((sum, m) => sum + m.quantity, 0) : 1)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Delivery Charges:</span>
                  <span className="font-mono font-medium text-text-primary">
                    {invoice?.currency_symbol || provider?.currencySymbol || currencySymbol}{(invoice.billing_info?.delivery_charges || 0).toFixed(2)}
                  </span>
                </div>

                <div className="border-t border-dashed border-border-primary/80 my-2" />

                <div className="flex justify-between items-center">
                  <span className="font-semibold text-text-primary">Total Amount:</span>
                  <span className="font-mono font-extrabold text-brand-red">
                    {invoice?.currency_symbol || provider?.currencySymbol || currencySymbol}{(invoice.total_amount !== undefined ? invoice.total_amount : (invoice.amount * 30)).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-text-primary">Paid Amount:</span>
                  <span className="font-mono font-extrabold text-success">
                    {invoice?.currency_symbol || provider?.currencySymbol || currencySymbol}{(invoice.paid_amount !== undefined ? invoice.paid_amount : (invoice.status?.toLowerCase() === "paid" ? (invoice.amount * 30) : 0)).toFixed(2)}
                  </span>
                </div>
                {invoice.due_amount !== undefined && (
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-text-primary">Due Amount:</span>
                    <span className="font-mono font-extrabold text-warning">
                      {invoice?.currency_symbol || provider?.currencySymbol || currencySymbol}{invoice.due_amount.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

            </div>
          )}
        </div>

        {/* Footer Download Button */}
        <div className="p-4 border-t border-border-primary bg-bg-secondary shrink-0">
          <button
            onClick={() => onDownload(invoice?.bill_unique_id || invoice?.id, invoice)}
            className="w-full py-3 bg-[#10B981] hover:bg-[#0D9488] active:scale-[0.99] text-white text-xs font-black rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            <Download className="w-4 h-4" />
            <span>Download Invoice PDF</span>
          </button>
        </div>

      </div>
    </div>
  );
}

// ==========================================
// 5. MEAL PLAN DETAIL MODAL
// ==========================================
export function MealPlanDetailModal({ isOpen, onClose, plan, isLoading = false, currencySymbol = "" }) {
  if (!isOpen) return null;

  const getStatusPill = (status) => {
    const normalized = status?.toLowerCase() || "";
    if (normalized === "active" || normalized === "1") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-[#EBFDF5] text-[#10B981] border border-[#10B981]/15">
          <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]"></span>
          Active
        </span>
      );
    } else if (normalized === "paused" || normalized === "2") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-[#FFFBEB] text-[#D97706] border border-[#D97706]/15">
          <span className="w-1.5 h-1.5 rounded-full bg-[#D97706]"></span>
          Paused
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-[#FEF2F2] text-[#EF4444] border border-[#EF4444]/15">
          <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444]"></span>
          Cancelled
        </span>
      );
    }
  };

  const formatDays = (daysArray) => {
    if (!daysArray || !Array.isArray(daysArray)) return "--";
    const dayLabels = { "1": "M", "2": "T", "3": "W", "4": "Th", "5": "F", "6": "Sa", "7": "Su" };
    return daysArray.map(d => dayLabels[d] || d).join(", ");
  };

  const address1 = plan?.delivery_address?.address_1 || "";
  const city = plan?.delivery_address?.city || "";
  const province = plan?.delivery_address?.province || "";
  const pinCode = plan?.delivery_address?.pin_code || "";
  const fullAddress = [address1, city, province, pinCode].filter(Boolean).join(", ") || "No address specified";

  const totalDeliveriesStr = plan?.number_of_deliveries || "Ongoing";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 overflow-y-auto">
      <div className="bg-[#FAF9F6] rounded-2xl border border-border-primary shadow-2xl max-w-lg w-full overflow-hidden flex flex-col max-h-[90vh] animate-modal-scale text-left">

        {/* Modal Header */}
        <div className="flex items-center justify-between p-5 border-b border-border-primary bg-white shrink-0">
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 rounded-lg bg-brand-red-light/30 flex items-center justify-center text-brand-red">
              <Sparkles className="w-4.5 h-4.5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-text-primary font-display leading-tight">
                Meal Plan Details
              </h3>
              <p className="text-[10px] text-text-muted mt-0.5 uppercase tracking-wider font-semibold">
                {plan?.meal_plan_unique_id ? `ID: ${plan.meal_plan_unique_id}` : "Subscription Details"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {plan && getStatusPill(plan.status_label || (plan.status === 1 ? "active" : "inactive"))}
            <button
              onClick={onClose}
              className="text-text-muted hover:text-text-primary rounded-full p-1.5 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-5 overflow-y-auto flex-1 space-y-6 scrollbar-thin">
          {isLoading ? (
            <div className="space-y-6 text-left">
              {/* Top Row Card Shimmer */}
              <div className="border border-border-primary/80 rounded-2xl p-4 flex gap-4 bg-white">
                <div className="flex-1 flex flex-col items-center space-y-2">
                  <div className="shimmer w-6 h-6 rounded-full"></div>
                  <div className="shimmer w-16 h-3 rounded"></div>
                  <div className="shimmer w-20 h-4 rounded"></div>
                </div>
                <div className="flex-1 flex flex-col items-center space-y-2 border-l border-border-primary/50">
                  <div className="shimmer w-6 h-6 rounded-full"></div>
                  <div className="shimmer w-16 h-3 rounded"></div>
                  <div className="shimmer w-20 h-4 rounded"></div>
                </div>
              </div>

              {/* Order Summary Shimmer */}
              <div className="space-y-2">
                <div className="shimmer w-24 h-3.5 rounded"></div>
                <div className="p-4 border border-border-primary/60 bg-white rounded-2xl flex flex-col space-y-2">
                  <div className="shimmer w-1/2 h-5 rounded"></div>
                  <div className="shimmer w-1/4 h-3 rounded"></div>
                  <div className="shimmer w-1/3 h-4.5 rounded"></div>
                  <div className="flex gap-2 pt-1.5">
                    <div className="shimmer w-16 h-5 rounded-lg"></div>
                    <div className="shimmer w-16 h-5 rounded-lg"></div>
                    <div className="shimmer w-24 h-5 rounded-lg"></div>
                  </div>
                </div>
              </div>

              {/* Preferences Shimmer */}
              <div className="space-y-2">
                <div className="shimmer w-20 h-3.5 rounded"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-border-primary/60 bg-white rounded-2xl">
                  <div className="space-y-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="flex gap-2">
                        <div className="shimmer w-4 h-4 rounded-full mt-0.5 shrink-0"></div>
                        <div className="space-y-1.5 flex-1">
                          <div className="shimmer w-12 h-2.5 rounded"></div>
                          <div className="shimmer w-20 h-3.5 rounded"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="flex gap-2">
                        <div className="shimmer w-4 h-4 rounded-full mt-0.5 shrink-0"></div>
                        <div className="space-y-1.5 flex-1">
                          <div className="shimmer w-12 h-2.5 rounded"></div>
                          <div className="shimmer w-20 h-3.5 rounded"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Billing & Pricing Shimmer */}
              <div className="space-y-2">
                <div className="shimmer w-28 h-3.5 rounded"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 border border-border-primary/60 bg-white rounded-2xl">
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <div className="shimmer w-4 h-4 rounded-full mt-0.5 shrink-0"></div>
                      <div className="space-y-1.5 flex-1">
                        <div className="shimmer w-16 h-2.5 rounded"></div>
                        <div className="shimmer w-20 h-3.5 rounded"></div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="shimmer w-4 h-4 rounded-full mt-0.5 shrink-0"></div>
                      <div className="space-y-1.5 flex-1">
                        <div className="shimmer w-16 h-2.5 rounded"></div>
                        <div className="shimmer w-20 h-3.5 rounded"></div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 border-t md:border-t-0 md:pl-4 md:border-l border-border-primary/60 pt-2 md:pt-0">
                    <div className="flex justify-between"><div className="shimmer w-12 h-3 rounded"></div><div className="shimmer w-10 h-3 rounded"></div></div>
                    <div className="flex justify-between"><div className="shimmer w-16 h-3 rounded"></div><div className="shimmer w-10 h-3 rounded flex justify-end"></div></div>
                    <div className="flex justify-between"><div className="shimmer w-14 h-3 rounded"></div><div className="shimmer w-10 h-3 rounded flex justify-end"></div></div>
                    <div className="flex justify-between"><div className="shimmer w-12 h-3 rounded"></div><div className="shimmer w-10 h-3 rounded flex justify-end"></div></div>
                    <div className="border-t border-border-primary/60 my-1 pt-1.5 flex justify-between"><div className="shimmer w-20 h-4 rounded"></div><div className="shimmer w-12 h-4 rounded"></div></div>
                  </div>
                </div>
              </div>
            </div>
          ) : !plan ? (
            <div className="py-12 text-center space-y-2">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto text-text-muted">
                <ShoppingBag className="w-6 h-6" />
              </div>
              <h4 className="text-sm font-bold text-text-primary">No Plan Data Available</h4>
            </div>
          ) : (
            <div className="space-y-6">

              {/* TOP ROW CARD: START DATE & DURATION */}
              <div className="bg-white border border-border-primary/80 rounded-2xl p-4 shadow-2xs divide-x divide-border-primary/50 flex text-center">
                <div className="flex-1 flex flex-col items-center justify-center py-1">
                  <CalendarDays className="w-5 h-5 text-text-secondary mb-1.5" />
                  <span className="text-[10px] text-text-secondary font-bold uppercase tracking-wider">Start Date</span>
                  <span className="text-xs font-bold text-text-primary mt-0.5">{plan.actual_start_date || plan.start_date || "--"}</span>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center py-1">
                  <Clock className="w-5 h-5 text-text-secondary mb-1.5" />
                  <span className="text-[10px] text-text-secondary font-bold uppercase tracking-wider">Duration</span>
                  <span className="text-xs font-bold text-text-primary mt-0.5">
                    {plan.number_of_deliveries ? "Number of Deliveries" : "Ongoing"}
                  </span>
                </div>
              </div>

              {/* 1. ORDER SUMMARY */}
              <div className="space-y-2">
                <h5 className="text-[10px] font-black uppercase tracking-wider text-text-secondary flex items-center gap-1.5">
                  <ClipboardList className="w-4 h-4 text-text-secondary" />
                  <span>Order Summary</span>
                </h5>
                <div className="flex items-center gap-4 p-4 bg-white border border-border-primary/60 rounded-2xl shadow-2xs">

                  <div className="flex-1 text-left min-w-0">
                    <h4 className="text-base font-black text-text-primary truncate leading-tight">
                      {plan.meal_name}
                    </h4>
                    <p className="text-[10px] text-text-muted mt-0.5 font-medium">1 * chhas</p>
                    <p className="text-xs text-text-secondary font-semibold font-mono mt-1">
                      {plan.currency_symbol || currencySymbol}{parseFloat(plan.plan_amount || 0).toFixed(2)} / meal • Qty: {plan.meal_quantity || 1}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-bg-secondary border border-border-primary text-[10px] font-bold text-text-secondary capitalize">
                        {plan.meal_type?.toLowerCase() === "lunch" ? <LunchSunIcon className="w-3 h-3 text-amber-700" /> : <DinnerMoonIcon className="w-3 h-3 text-[#6366F1]" />}
                        {plan.meal_type}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-bg-secondary border border-border-primary text-[10px] font-bold text-text-secondary capitalize">
                        {plan.delivery_preference?.toLowerCase() === "pickup" ? <PickupStoreIcon className="w-3 h-3 text-text-secondary" /> : <Truck className="w-3 h-3 text-text-secondary" />}
                        {plan.delivery_preference}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-brand-red-light/35 border border-brand-red/10 text-[10px] font-bold text-brand-red">
                        <CheckCircle2 className="w-3 h-3" />
                        Consumed: {plan.meals_consumed} / {totalDeliveriesStr}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. PREFERENCES */}
              <div className="space-y-2">
                <h5 className="text-[10px] font-black uppercase tracking-wider text-text-secondary flex items-center gap-1.5">
                  <PickupStoreIcon className="w-4 h-4 text-text-secondary" />
                  <span>Preferences</span>
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-white border border-border-primary/60 rounded-2xl shadow-2xs text-left">

                  {/* Left Column */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Utensils className="w-3 h-3 text-text-secondary mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-[10px] text-text-muted font-bold uppercase tracking-wider block">Meal Timing</span>
                        <span className="text-xs font-bold text-text-primary capitalize mt-0.5 block">{plan.meal_type || "--"}</span>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Calendar className="w-3 h-3 text-text-secondary mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-[10px] text-text-muted font-bold uppercase tracking-wider block">Delivery Days</span>
                        <span className="text-xs font-bold text-text-primary mt-0.5 block">{formatDays(plan.days)}</span>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <MapPin className="w-3 h-3 text-text-secondary mt-0.5 shrink-0" />
                      <div className="min-w-0 relative group/addr">
                        <span className="text-[10px] text-text-muted font-bold uppercase tracking-wider block">Address</span>
                        <span className="text-xs font-bold text-text-primary mt-0.5 block truncate max-w-[150px] cursor-help">
                          {fullAddress}
                        </span>
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover/addr:block w-64 bg-gray-900/95 text-white text-[10px] p-2.5 rounded-lg shadow-xl z-50 leading-relaxed font-medium transition-all duration-200">
                          {fullAddress}
                          <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-900/95"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Clock className="w-3 h-3 text-text-secondary mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-[10px] text-text-muted font-bold uppercase tracking-wider block">Time Slot</span>
                        <span className="text-xs font-bold text-text-primary mt-0.5 block">{plan.time_slot || "--"}</span>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      {plan.delivery_preference?.toLowerCase() === "pickup" ? (
                        <PickupStoreIcon className="w-3 h-3 text-text-secondary mt-0.5 shrink-0" />
                      ) : (
                        <Truck className="w-3 h-3 text-text-secondary mt-0.5 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <span className="text-[10px] text-text-muted font-bold uppercase tracking-wider block">Delivery Type</span>
                        <span className="text-xs font-bold text-text-primary capitalize mt-0.5 block">{plan.delivery_preference || "--"}</span>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Hourglass className="w-3 h-3 text-text-secondary mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-[10px] text-text-muted font-bold uppercase tracking-wider block">Duration</span>
                        <span className="text-xs font-bold text-text-primary capitalize mt-0.5 block">
                          {plan.number_of_deliveries ? "Number of Deliveries" : "Ongoing"}
                        </span>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* 3. BILLING & PRICING */}
              <div className="space-y-2">
                <h5 className="text-[10px] font-black uppercase tracking-wider text-text-secondary flex items-center gap-1.5">
                  <CreditCard className="w-4 h-4 text-text-secondary" />
                  <span>Billing & Pricing</span>
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-white border border-border-primary/60 rounded-2xl shadow-2xs text-left">

                  {/* Left Billing Details */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Calendar className="w-3 h-3 text-text-secondary mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-[10px] text-text-muted font-bold uppercase tracking-wider block">Billing Frequency</span>
                        <span className="text-xs font-bold text-text-primary capitalize mt-0.5 block">{plan.billing_type || "Package"}</span>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <CalendarDays className="w-3 h-3 text-text-secondary mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-[10px] text-text-muted font-bold uppercase tracking-wider block">Billing Day</span>
                        <span className="text-xs font-bold text-text-primary mt-0.5 block">{plan.billing_day || "--"}</span>
                      </div>
                    </div>
                  </div>

                  {/* Right Pricing Breakdown */}
                  <div className="space-y-2 pt-1 border-t md:border-t-0 md:pl-4 md:border-l border-border-primary/60">
                    <div className="flex justify-between text-xs text-text-secondary">
                      <span>Meal Price</span>
                      <span className="font-semibold text-text-primary font-mono">{plan.currency_symbol || currencySymbol}{parseFloat(plan.plan_amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-text-secondary">
                      <span>Add-on Items</span>
                      <span className="font-semibold text-success font-mono">+ {plan.currency_symbol || currencySymbol}0.00</span>
                    </div>
                    <div className="flex justify-between text-xs text-text-secondary">
                      <span>Delivery Charge</span>
                      <span className="font-semibold text-text-muted font-mono">- {plan.currency_symbol || currencySymbol}0.00</span>
                    </div>
                    <div className="flex justify-between text-xs text-text-secondary">
                      <span>Other Charges</span>
                      <span className="font-semibold text-success font-mono">+ {plan.currency_symbol || currencySymbol}0.00</span>
                    </div>
                    <div className="h-px bg-border-primary/60 my-2" />
                    <div className="flex justify-between text-sm font-black text-text-primary">
                      <span>Final Amount (per delivery)</span>
                      <span className="font-mono text-brand-red">{plan.currency_symbol || currencySymbol}{parseFloat(plan.plan_amount || 0).toFixed(2)}</span>
                    </div>
                  </div>

                </div>
              </div>

              {/* 4. REMARKS */}
              <div className="space-y-2">
                <h5 className="text-[10px] font-black uppercase tracking-wider text-text-secondary flex items-center gap-1.5">
                  <MessageSquare className="w-4 h-4 text-text-secondary" />
                  <span>Remarks</span>
                </h5>
                <div className="p-4 bg-white border border-border-primary/60 rounded-2xl shadow-2xs text-left">
                  <span className="text-[10px] text-text-muted font-bold uppercase tracking-wider block">Delivery notes or instructions</span>
                  <span className="text-xs font-bold text-text-primary mt-1 block italic capitalize">
                    {plan.remarks || plan.delivery_notes || "done"}
                  </span>
                </div>
              </div>

            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-border-primary bg-bg-secondary shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2.5 px-4 bg-white border border-border-primary text-text-secondary text-xs font-bold rounded-xl hover:bg-gray-100 transition-colors active:scale-98 cursor-pointer shadow-sm uppercase tracking-wider"
          >
            Close
          </button>
        </div>

      </div>
    </div>
  );
}
